import React, { useState, useMemo, useEffect } from 'react';
import { Brain, Play, Trophy, Clock, Target, Search, Users, Award, BookOpen } from 'lucide-react';
import quizData from '../data/quizdata.json';
import quizData1 from '../data/quizdata1.json';
import quizData2 from '../data/quizdata2.json';

interface Question {
  id: number;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

interface Quiz {
  id: string;
  title: string;
  description: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  duration: number; // in minutes
  questions: Question[];
  category: string;
}

const QuizProgram: React.FC = () => {
  const [selectedQuiz, setSelectedQuiz] = useState<Quiz | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [quizStarted, setQuizStarted] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showRegistration, setShowRegistration] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('All');
  
  // Timer states
  const [timeRemaining, setTimeRemaining] = useState(0); // in seconds
  const [timerActive, setTimerActive] = useState(false);

  // Combine all quiz data from multiple files
  const allQuizzes = useMemo(() => {
    const combinedQuizzes = [
      ...quizData.quizzes,
      ...quizData1.quizzes,
      ...quizData2.quizzes
    ] as Quiz[];

    // Remove duplicates based on quiz ID
    const uniqueQuizzes = combinedQuizzes.filter((quiz, index, self) => 
      index === self.findIndex(q => q.id === quiz.id)
    );

    return uniqueQuizzes;
  }, []);

  // Combine categories from all files and remove duplicates
  const categories = useMemo(() => {
    const allCategories = [
      ...quizData.categories,
      ...quizData1.categories,
      ...quizData2.categories
    ];
    const uniqueCategories = [...new Set(allCategories)];
    return ['All', ...uniqueCategories.sort()];
  }, []);

  // Combine difficulties from all files and remove duplicates
  const difficulties = useMemo(() => {
    const allDifficulties = [
      ...quizData.difficulties,
      ...quizData1.difficulties,
      ...quizData2.difficulties
    ];
    const uniqueDifficulties = [...new Set(allDifficulties)];
    return ['All', ...uniqueDifficulties];
  }, []);

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (timerActive && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            // Time's up - auto submit quiz
            setTimerActive(false);
            setShowResults(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [timerActive, timeRemaining]);

  // Format time display
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Get timer color based on remaining time
  const getTimerColor = () => {
    if (!selectedQuiz) return 'text-muted-foreground';
    
    const totalTime = selectedQuiz.duration * 60;
    const percentage = (timeRemaining / totalTime) * 100;
    
    if (percentage > 50) return 'text-success';
    if (percentage > 25) return 'text-warning';
    return 'text-error';
  };

  // Filter quizzes based on search query, category, and difficulty
  const filteredQuizzes = useMemo(() => {
    return allQuizzes.filter(quiz => {
      const matchesSearch = !searchQuery.trim() || 
        quiz.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
        quiz.difficulty.toLowerCase().includes(searchQuery.toLowerCase()) ||
        quiz.category.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = selectedCategory === 'All' || quiz.category === selectedCategory;
      const matchesDifficulty = selectedDifficulty === 'All' || quiz.difficulty === selectedDifficulty;
      
      return matchesSearch && matchesCategory && matchesDifficulty;
    });
  }, [allQuizzes, searchQuery, selectedCategory, selectedDifficulty]);

  const startQuiz = (quiz: Quiz) => {
    setSelectedQuiz(quiz);
    setCurrentQuestionIndex(0);
    setSelectedAnswers([]);
    setShowResults(false);
    setQuizStarted(true);
    
    // Initialize timer
    const totalTimeInSeconds = quiz.duration * 60;
    setTimeRemaining(totalTimeInSeconds);
    setTimerActive(true);
  };

  const selectAnswer = (answerIndex: number) => {
    const newAnswers = [...selectedAnswers];
    newAnswers[currentQuestionIndex] = answerIndex;
    setSelectedAnswers(newAnswers);
  };

  const nextQuestion = () => {
    if (selectedQuiz && currentQuestionIndex < selectedQuiz.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // Quiz completed - stop timer and show results
      setTimerActive(false);
      setShowResults(true);
    }
  };

  const resetQuiz = () => {
    setSelectedQuiz(null);
    setCurrentQuestionIndex(0);
    setSelectedAnswers([]);
    setShowResults(false);
    setQuizStarted(false);
    setTimerActive(false);
    setTimeRemaining(0);
  };

  const calculateScore = () => {
    if (!selectedQuiz) return 0;
    let correct = 0;
    selectedQuiz.questions.forEach((question, index) => {
      if (selectedAnswers[index] === question.correctAnswer) {
        correct++;
      }
    });
    return Math.round((correct / selectedQuiz.questions.length) * 100);
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Beginner': return 'text-success bg-success/10';
      case 'Intermediate': return 'text-warning bg-warning/10';
      case 'Advanced': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted/10';
    }
  };

  // Registration Form Component
  const RegistrationForm = () => {
    const [formData, setFormData] = useState({
      name: '',
      email: '',
      phone: '',
      course: '',
      experience: ''
    });

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      
      const message = `Quiz Program Registration:
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
Interested Course: ${formData.course}
Experience Level: ${formData.experience}`;

      const whatsappUrl = `https://wa.me/919698862115?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
      
      // Reset form and go back to quiz selection
      setFormData({ name: '', email: '', phone: '', course: '', experience: '' });
      setShowRegistration(false);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = e.target;
      setFormData(prev => ({ ...prev, [name]: value }));
    };

    return (
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="mb-6 rounded-full bg-primary/10 p-4 inline-block">
            <Users className="text-primary w-12 h-12" strokeWidth={2} />
          </div>
          <h2 className="text-3xl font-bold mb-4">Register for Quiz Program</h2>
          <p className="text-muted-foreground">
            Join our comprehensive quiz program and enhance your skills with structured learning.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-card rounded-lg p-6 shadow-subtle dark:shadow-subtle-dark border border-border space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium mb-2">Full Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-2">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium mb-2">Phone Number *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background"
              />
            </div>

            <div>
              <label htmlFor="course" className="block text-sm font-medium mb-2">Interested Course *</label>
              <select
                id="course"
                name="course"
                required
                value={formData.course}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background"
              >
                <option value="">Select Course</option>
                <option value="Web Development">Web Development</option>
                <option value="Programming">Programming</option>
                <option value="Database">Database</option>
                <option value="Blockchain">Blockchain</option>
                <option value="All Courses">All Courses</option>
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="experience" className="block text-sm font-medium mb-2">Experience Level *</label>
            <select
              id="experience"
              name="experience"
              required
              value={formData.experience}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background"
            >
              <option value="">Select Experience Level</option>
              <option value="Beginner">Beginner</option>
              <option value="Intermediate">Intermediate</option>
              <option value="Advanced">Advanced</option>
            </select>
          </div>

          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => setShowRegistration(false)}
              className="flex-1 px-6 py-3 border border-border rounded-md hover:bg-muted transition-colors"
            >
              Back to Quizzes
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              Register Now
            </button>
          </div>
        </form>
      </div>
    );
  };

  // Show registration form if requested
  if (showRegistration) {
    return <RegistrationForm />;
  }

  // Quiz Selection View
  if (!selectedQuiz || !quizStarted) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12 animated-section">
          <div className="mb-8 rounded-full bg-primary/10 p-4 inline-block">
            <Brain className="text-primary w-12 h-12 md:w-16 md:h-16" strokeWidth={2} />
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Interactive Quiz Program
          </h2>
          
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Test your knowledge with our comprehensive quiz collection. Choose from various topics 
            and difficulty levels to challenge yourself and track your progress.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setShowRegistration(true)}
              className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              <Users className="w-5 h-5" />
              Register for Program
            </button>
            <button className="flex items-center gap-2 px-6 py-3 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition-colors">
              <BookOpen className="w-5 h-5" />
              View Certificates
            </button>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="mb-8 max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
              <input
                type="text"
                placeholder="Search by title, difficulty, or category..."
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-3 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            <select
              value={selectedDifficulty}
              onChange={(e) => setSelectedDifficulty(e.target.value)}
              className="px-4 py-3 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {difficulties.map(difficulty => (
                <option key={difficulty} value={difficulty}>{difficulty}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="bg-card rounded-lg p-4 shadow-subtle dark:shadow-subtle-dark border border-border text-center">
            <div className="text-2xl font-bold text-primary mb-1">{allQuizzes.length}</div>
            <div className="text-sm text-muted-foreground">Total Quizzes</div>
          </div>
          <div className="bg-card rounded-lg p-4 shadow-subtle dark:shadow-subtle-dark border border-border text-center">
            <div className="text-2xl font-bold text-success mb-1">{categories.length - 1}</div>
            <div className="text-sm text-muted-foreground">Categories</div>
          </div>
          <div className="bg-card rounded-lg p-4 shadow-subtle dark:shadow-subtle-dark border border-border text-center">
            <div className="text-2xl font-bold text-warning mb-1">{difficulties.length - 1}</div>
            <div className="text-sm text-muted-foreground">Difficulty Levels</div>
          </div>
          <div className="bg-card rounded-lg p-4 shadow-subtle dark:shadow-subtle-dark border border-border text-center">
            <div className="text-2xl font-bold text-accent mb-1">Free</div>
            <div className="text-sm text-muted-foreground">Access</div>
          </div>
        </div>

        {/* Quiz Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredQuizzes.map((quiz) => (
            <div
              key={quiz.id}
              className="bg-card rounded-lg p-6 shadow-subtle dark:shadow-subtle-dark border border-border hover:shadow-lg transition-all duration-300 animated-section group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                    {quiz.title}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-3 line-clamp-3">
                    {quiz.description}
                  </p>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getDifficultyColor(quiz.difficulty)}`}>
                    {quiz.difficulty}
                  </span>
                  <span className="text-sm text-muted-foreground">{quiz.category}</span>
                </div>
                
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>{quiz.duration} min</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Target className="w-4 h-4" />
                    <span>{quiz.questions.length} questions</span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => startQuiz(quiz)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              >
                <Play className="w-4 h-4" />
                Start Quiz
              </button>
            </div>
          ))}
        </div>

        {filteredQuizzes.length === 0 && (
          <div className="text-center py-12">
            <Brain className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">No quizzes found</h3>
            <p className="text-muted-foreground">
              Try adjusting your search terms or filters to find relevant quizzes.
            </p>
          </div>
        )}
      </div>
    );
  }

  // Results View
  if (showResults) {
    const score = calculateScore();
    const correctAnswers = selectedQuiz.questions.filter((question, index) => 
      selectedAnswers[index] === question.correctAnswer
    ).length;

    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12 animated-section">
          <div className="mb-8 rounded-full bg-primary/10 p-4 inline-block">
            <Trophy className="text-primary w-12 h-12 md:w-16 md:h-16" strokeWidth={2} />
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Quiz Complete!
          </h2>
          
          <div className="bg-card rounded-lg p-8 shadow-subtle dark:shadow-subtle-dark border border-border mb-8">
            <h3 className="text-2xl font-semibold mb-4">{selectedQuiz.title}</h3>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-3xl font-bold text-primary mb-2">{score}%</div>
                <div className="text-muted-foreground">Overall Score</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-success mb-2">{correctAnswers}</div>
                <div className="text-muted-foreground">Correct Answers</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-error mb-2">{selectedQuiz.questions.length - correctAnswers}</div>
                <div className="text-muted-foreground">Incorrect Answers</div>
              </div>
            </div>

            {/* Certificate Message */}
            {score >= 80 && (
              <div className="mt-6 p-4 bg-success/10 border border-success/20 rounded-lg">
                <div className="flex items-center justify-center gap-2 text-success">
                  <Award className="w-5 h-5" />
                  <span className="font-medium">Congratulations! You've earned a certificate!</span>
                </div>
                <p className="text-sm text-muted-foreground text-center mt-2">
                  Contact us to receive your official completion certificate.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Detailed Results */}
        <div className="space-y-6 mb-8">
          <h3 className="text-2xl font-semibold">Review Your Answers</h3>
          {selectedQuiz.questions.map((question, index) => {
            const userAnswer = selectedAnswers[index];
            const isCorrect = userAnswer === question.correctAnswer;
            
            return (
              <div key={question.id} className="bg-card rounded-lg p-6 shadow-subtle dark:shadow-subtle-dark border border-border">
                <div className="flex items-start justify-between mb-4">
                  <h4 className="text-lg font-semibold flex-1 pr-4">
                    {index + 1}. {question.question}
                  </h4>
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isCorrect ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
                  }`}>
                    {isCorrect ? 'Correct' : 'Incorrect'}
                  </div>
                </div>
                
                <div className="space-y-2 mb-4">
                  {question.options.map((option, optionIndex) => {
                    let className = 'p-3 rounded-md border ';
                    
                    if (optionIndex === question.correctAnswer) {
                      className += 'border-success bg-success/10 text-success';
                    } else if (optionIndex === userAnswer && !isCorrect) {
                      className += 'border-error bg-error/10 text-error';
                    } else {
                      className += 'border-border bg-muted/30';
                    }
                    
                    return (
                      <div key={optionIndex} className={className}>
                        {option}
                        {optionIndex === question.correctAnswer && (
                          <span className="ml-2 text-success">✓ Correct Answer</span>
                        )}
                        {optionIndex === userAnswer && !isCorrect && (
                          <span className="ml-2 text-error">✗ Your Answer</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                <div className="p-4 bg-muted/30 rounded-md">
                  <p className="text-sm text-muted-foreground">
                    <strong>Explanation:</strong> {question.explanation}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center space-y-4">
          <button
            onClick={resetQuiz}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors mr-4"
          >
            Take Another Quiz
          </button>
          {score >= 80 && (
            <button
              onClick={() => {
                const message = `Certificate Request - Quiz Completion:
Quiz: ${selectedQuiz.title}
Score: ${score}%
Completion Date: ${new Date().toLocaleDateString()}

Please provide my completion certificate.`;
                const whatsappUrl = `https://wa.me/919698862115?text=${encodeURIComponent(message)}`;
                window.open(whatsappUrl, '_blank');
              }}
              className="px-6 py-3 bg-success text-success-foreground rounded-md hover:bg-success/90 transition-colors"
            >
              Request Certificate
            </button>
          )}
        </div>
      </div>
    );
  }

  // Quiz Taking View
  const currentQuestion = selectedQuiz.questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / selectedQuiz.questions.length) * 100;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Quiz Header with Timer */}
      <div className="mb-8 animated-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">{selectedQuiz.title}</h2>
          <div className="flex items-center gap-4">
            {/* Timer Display */}
            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-card ${getTimerColor()}`}>
              <Clock className="w-5 h-5" />
              <span className="font-mono text-lg font-bold">
                {formatTime(timeRemaining)}
              </span>
            </div>
            <button
              onClick={resetQuiz}
              className="px-4 py-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              Exit Quiz
            </button>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium">
              Question {currentQuestionIndex + 1} of {selectedQuiz.questions.length}
            </span>
            <span className="text-sm text-muted-foreground">
              {Math.round(progress)}% Complete
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Time Warning */}
        {timeRemaining <= 300 && timeRemaining > 0 && (
          <div className="mb-4 p-3 bg-warning/10 border border-warning/20 rounded-lg">
            <div className="flex items-center gap-2 text-warning">
              <Clock className="w-4 h-4" />
              <span className="text-sm font-medium">
                {timeRemaining <= 60 
                  ? `Only ${timeRemaining} seconds remaining!` 
                  : `Less than ${Math.ceil(timeRemaining / 60)} minutes remaining!`
                }
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Question */}
      <div className="bg-card rounded-lg p-8 shadow-subtle dark:shadow-subtle-dark border border-border mb-8 animated-section">
        <h3 className="text-xl font-semibold mb-6">{currentQuestion.question}</h3>
        
        <div className="space-y-3">
          {currentQuestion.options.map((option, index) => (
            <button
              key={index}
              onClick={() => selectAnswer(index)}
              className={`w-full p-4 text-left rounded-md border transition-all duration-200 ${
                selectedAnswers[currentQuestionIndex] === index
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border hover:border-primary/50 hover:bg-primary/5'
              }`}
            >
              <div className="flex items-center">
                <div className={`w-6 h-6 rounded-full border-2 mr-3 flex items-center justify-center ${
                  selectedAnswers[currentQuestionIndex] === index
                    ? 'border-primary bg-primary'
                    : 'border-muted-foreground'
                }`}>
                  {selectedAnswers[currentQuestionIndex] === index && (
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  )}
                </div>
                {option}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">
          {selectedAnswers[currentQuestionIndex] !== undefined ? 'Answer selected' : 'Select an answer to continue'}
        </div>
        
        <button
          onClick={nextQuestion}
          disabled={selectedAnswers[currentQuestionIndex] === undefined}
          className="px-6 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {currentQuestionIndex === selectedQuiz.questions.length - 1 ? 'Finish Quiz' : 'Next Question'}
        </button>
      </div>
    </div>
  );
};

export default QuizProgram;